import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/shared/components';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { cn } from '@/shared/utils/styling';

export interface ConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  description: string;
  confirmText?: string;
  cancelText?: string;
  confirmVariant?: 'default' | 'destructive';
  isLoading?: boolean;
  showCancelButton?: boolean;
  onAnimationEnd?: (event: React.AnimationEvent<HTMLDivElement>) => void;
  className?: string;
}

export function ConfirmationModal({
  isOpen,
  onClose,
  onConfirm,
  title,
  description,
  confirmText,
  cancelText,
  confirmVariant = 'default',
  isLoading = false,
  showCancelButton = true,
  onAnimationEnd,
  className,
}: ConfirmationModalProps) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent
        className={cn(
          'w-[38.4rem] py-[2.7rem] sm:py-[3.4rem] px-[3.2rem] gap-[3.2rem] rounded-[2.4rem] shadow-lg',
          className
        )}
        onAnimationEnd={onAnimationEnd}>
        <DialogHeader className="flex flex-row justify-between">
          {title ? (
            <DialogTitle className="headline1 text-foreground text-left">
              {title}
            </DialogTitle>
          ) : (
            <VisuallyHidden>
              <DialogTitle>Confirm Dialog</DialogTitle>
            </VisuallyHidden>
          )}
        </DialogHeader>

        <div className="flex flex-col gap-[3.2rem]">
          <p className="p3 text-muted-foreground">{description}</p>

          <div className="flex flex-col gap-[1.6rem]">
            <Button
              onClick={onConfirm}
              variant={confirmVariant}
              disabled={isLoading}
              className="w-full h-[5.2rem] rounded-[1.2rem] headline4"
              loading={isLoading}>
              {confirmText || ''}
            </Button>
            {showCancelButton && (
              <Button
                onClick={onClose}
                variant="secondary"
                disabled={isLoading}
                className="w-full h-[5.2rem] rounded-[1.2rem] headline4">
                {cancelText || ''}
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
